import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSite } from '../../context/SiteContext';

const Catalogs = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { getCurrentSiteId, currentSite } = useSite();
  const [type, setType] = useState('lines');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      
      // Filtrar por site_id solo si existe
      const siteId = getCurrentSiteId();
      console.log('🔍 Catalogs - Loading:', { type, siteId, currentSite });
      
      const params = {};
      if (siteId) {
        params.site_id = siteId;
      }
      // Si no hay siteId, no agregar el parámetro (admin_master verá todos los catálogos)
      
      const { data } = await axios.get(`/catalogs/${type}`, { params });
      console.log('✅ Catalogs - Data loaded:', data);
      setItems(data);
    } catch (error) {
      console.error('❌ Catalogs - Error loading:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [type, getCurrentSiteId, currentSite]);

  // Cargar datos cuando cambia el tipo de catálogo o el sitio
  useEffect(() => {
    if (isAuthenticated()) {
      load();
    }
  }, [type, currentSite, isAuthenticated, load]);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onCreate = async () => {
    const siteId = getCurrentSiteId();
    const payload = { ...form };
    
    // Agregar site_id si está disponible
    if (siteId) {
      payload.site_id = siteId;
    }
    
    await axios.post(`/catalogs/${type}`, payload);
    setForm({ code: '', name: '' });
    load();
  };

  const onUpdate = async () => {
    await axios.put(`/catalogs/${type}/${editing}`, form);
    setEditing(null);
    setForm({ code: '', name: '' });
    load();
  };

  const onDelete = async (id) => {
    if (!window.confirm(t('catalogs.messages.deleteConfirm'))) return;
    await axios.delete(`/catalogs/${type}/${id}`);
    load();
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({ code: it.code || '', name: it.name || it.name });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-extrabold text-blue-900">{t('catalogs.title')}</h1>
        {currentSite && (
          <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold border-2 border-blue-300">
            📍 {currentSite.code} - {currentSite.name}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <select className="rounded border border-gray-300 px-3 py-2 text-gray-900 font-semibold focus:ring-2 focus:ring-blue-400 bg-white" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="lines">{t('catalogs.tabs.lines')}</option>
          <option value="shifts">{t('catalogs.tabs.shifts')}</option>
          <option value="products">{t('catalogs.tabs.products')}</option>
          <option value="downtime_codes">{t('catalogs.tabs.downtimeCodes')}</option>
          <option value="defect_types">{t('catalogs.tabs.defectTypes')}</option>
        </select>
        <button className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition" onClick={load}>{t('catalogs.buttons.refresh')}</button>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-blue-100">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('catalogs.form.code')}</label>
            <input name="code" className="w-32 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={form.code} onChange={onChange} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('catalogs.form.name')}</label>
            <input name="name" className="w-56 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={form.name} onChange={onChange} />
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button className="px-4 py-2 bg-blue-700 text-white font-bold rounded-lg shadow hover:bg-blue-800 transition" onClick={onUpdate}>{t('catalogs.buttons.save')}</button>
                <button className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition" onClick={() => { setEditing(null); setForm({ code: '', name: '' }); }}>{t('catalogs.buttons.cancel')}</button>
              </>
            ) : (
              <button className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg shadow hover:bg-green-700 transition" onClick={onCreate}>{t('catalogs.buttons.add')}</button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border border-gray-200 rounded-xl">
            <thead className="bg-blue-100 text-blue-900">
              <tr>
                <th className="px-4 py-2 font-bold">{t('catalogs.table.code')}</th>
                <th className="px-4 py-2 font-bold">{t('catalogs.table.name')}</th>
                <th className="px-4 py-2 font-bold">{t('catalogs.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.loading')}...
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                    {t('catalogs.messages.noData')}
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr key={it.id} className="even:bg-gray-50">
                    <td className="px-4 py-2">{it.code || ''}</td>
                    <td className="px-4 py-2">{it.name}</td>
                    <td className="px-4 py-2 flex gap-2">
                      <button className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg font-semibold hover:bg-blue-100 transition" onClick={() => startEdit(it)}>{t('catalogs.buttons.edit')}</button>
                      <button className="px-3 py-1 bg-red-50 text-red-700 border border-red-300 rounded-lg font-semibold hover:bg-red-100 transition" onClick={() => onDelete(it.id)}>{t('catalogs.buttons.delete')}</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Catalogs;
